import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiFilter, FiEdit, FiLayers, FiX, FiSearch, FiBookOpen } from 'react-icons/fi';

import Layout from '../components/Layout';
import theme from '../styles/theme';
import { recursosAPI } from '../services/api';
import { VeiculosPaginacao } from '../components/veiculos';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing[3]};
  flex-wrap: wrap;
`;

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  color: ${theme.colors.text.primary};
  margin: 0;
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.md};
  color: ${theme.colors.text.muted};
  margin: 0;
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[5]};
  border-radius: ${theme.borderRadius.xl};
  border: ${({ $variant }) =>
    $variant === 'secondary' ? `1px solid ${theme.colors.primary[500]}` : 'none'};
  background: ${({ $variant }) =>
    $variant === 'primary' ? theme.colors.primary[500] : 'transparent'};
  color: ${({ $variant }) =>
    $variant === 'primary' ? theme.colors.text.primary : theme.colors.primary[500]};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ $variant }) =>
      $variant === 'primary' ? theme.colors.primary[600] : theme.colors.primary[500]}15;
  }
`;

const FiltersCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[4]};
  align-items: flex-end;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  flex: 1;
  min-width: 240px;
`;

const Label = styled.label`
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
`;

const Input = styled.input`
  padding: ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }
`;

const FilterActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-wrap: wrap;
`;

const FilterButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  background: transparent;
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ContentCard = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius['2xl']};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const TableWrapper = styled.div`
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  min-width: 720px;
  border-collapse: collapse;
`;

const Thead = styled.thead`
  background: ${theme.colors.background.primary};
`;

const Th = styled.th`
  text-align: left;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  border-bottom: 1px solid ${theme.colors.border.primary};
`;

const Td = styled.td`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border.primary};
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.secondary};
`;

const ActionsCell = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const GhostButton = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: all 0.2s ease;

  &:hover {
    border-color: ${theme.colors.primary[500]};
    color: ${theme.colors.primary[600]};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[10]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
`;

const EmptyTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  color: ${theme.colors.text.primary};
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: ${theme.fontSizes.base};
  color: ${theme.colors.text.muted};
  max-width: 360px;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${theme.zIndex.modal};
`;

const ModalContent = styled.div`
  width: 100%;
  max-width: 540px;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius['2xl']};
  border: 1px solid ${theme.colors.border.primary};
  box-shadow: ${theme.shadows.lg};
  padding: ${theme.spacing[5]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  color: ${theme.colors.text.primary};
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.lg};
  cursor: pointer;
`;

const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[3]};
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  max-height: 320px;
  overflow-y: auto;
`;

const ListItem = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.primary};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[1]};
`;

const Recursos = () => {
  const navigate = useNavigate();
  const [recursos, setRecursos] = useState([]);
  const [filtros, setFiltros] = useState({
    search: '',
    page: 1,
    limit: 10,
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState(null);
  const [modal, setModal] = useState({
    aberto: false,
    recurso: null,
    planos: [],
    loading: false,
    erro: null,
  });

  const carregarRecursos = async (params = filtros) => {
    try {
      setLoading(true);
      setErro(null);
      const response = await recursosAPI.listar(params);
      if (response.data?.success) {
        setRecursos(response.data.data || []);
        setPagination(response.data.pagination || pagination);
      } else {
        setErro('Não foi possível carregar os recursos.');
      }
    } catch (error) {
      console.error('Erro ao carregar recursos', error);
      setErro('Erro ao carregar recursos. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregarRecursos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFiltroChange = (campo, valor) => {
    setFiltros((prev) => ({
      ...prev,
      [campo]: valor,
    }));
  };

  const handleAplicarFiltro = () => {
    const params = { ...filtros, page: 1 };
    setFiltros(params);
    carregarRecursos(params);
  };

  const handleLimparFiltro = () => {
    const params = { search: '', page: 1, limit: filtros.limit };
    setFiltros(params);
    carregarRecursos(params);
  };

  const handlePageChange = (nextPage) => {
    const params = { ...filtros, page: nextPage };
    setFiltros(params);
    carregarRecursos(params);
  };

  const handleItemsPerPageChange = (newLimit) => {
    const params = { ...filtros, limit: newLimit, page: 1 };
    setFiltros(params);
    carregarRecursos(params);
  };

  const abrirModalPlanos = async (recurso) => {
    setModal({
      aberto: true,
      recurso,
      planos: [],
      loading: true,
      erro: null,
    });

    try {
      const response = await recursosAPI.listarPlanos(recurso.id);
      if (response.data?.success) {
        setModal((prev) => ({
          ...prev,
          planos: response.data.data || [],
          loading: false,
        }));
      } else {
        throw new Error('Erro ao carregar planos associados.');
      }
    } catch (error) {
      console.error('Erro ao carregar planos do recurso', error);
      setModal((prev) => ({
        ...prev,
        loading: false,
        erro: 'Não foi possível carregar os planos associados.',
      }));
    }
  };

  const fecharModal = () => {
    setModal({
      aberto: false,
      recurso: null,
      planos: [],
      loading: false,
      erro: null,
    });
  };

  const listaVazia = !loading && recursos.length === 0;

  return (
    <Layout title="Recursos do Catálogo">
      <Container>
        <Header>
          <TitleGroup>
            <Title>Recursos disponíveis</Title>
            <Subtitle>
              Mantenha o catálogo de funcionalidades que podem ser vinculadas aos planos comerciais.
            </Subtitle>
          </TitleGroup>
          <Button $variant="primary" onClick={() => navigate('/gestao/recursos/novo')}>
            <FiPlus />
            Novo recurso
          </Button>
        </Header>

        <FiltersCard>
          <FilterRow>
            <FilterGroup>
              <Label>Buscar por código ou descrição</Label>
              <Input
                value={filtros.search}
                onChange={(e) => handleFiltroChange('search', e.target.value)}
                placeholder="Ex.: API_WHATSAPP"
              />
            </FilterGroup>
            <FilterActions>
              <FilterButton onClick={handleLimparFiltro} disabled={loading}>
                <FiX />
                Limpar Filtros
              </FilterButton>
              <FilterButton onClick={handleAplicarFiltro} disabled={loading}>
                <FiSearch />
                {loading ? 'Buscando...' : 'Buscar'}
              </FilterButton>
            </FilterActions>
          </FilterRow>
        </FiltersCard>

        <ContentCard>
          {erro && (
            <EmptyState>
              <EmptyIcon>⚠️</EmptyIcon>
              <EmptyTitle>Falha ao carregar recursos</EmptyTitle>
              <EmptyText>{erro}</EmptyText>
              <Button $variant="primary" onClick={() => carregarRecursos()}>
                Tentar novamente
              </Button>
            </EmptyState>
          )}

          {loading && !erro && (
            <EmptyState>
              <EmptyIcon>
                <FiLayers />
              </EmptyIcon>
              <EmptyTitle>Carregando recursos</EmptyTitle>
              <EmptyText>Aguarde enquanto buscamos a lista de recursos disponíveis.</EmptyText>
            </EmptyState>
          )}

          {listaVazia && !erro && (
            <EmptyState>
              <EmptyIcon>
                <FiLayers />
              </EmptyIcon>
              <EmptyTitle>Nenhum recurso cadastrado</EmptyTitle>
              <EmptyText>
                Cadastre recursos para montar a composição dos planos comerciais.
              </EmptyText>
              <Button $variant="primary" onClick={() => navigate('/gestao/recursos/novo')}>
                <FiPlus />
                Cadastrar recurso
              </Button>
            </EmptyState>
          )}

          {!loading && !erro && recursos.length > 0 && (
            <>
              <TableWrapper>
                <Table>
                  <Thead>
                    <tr>
                      <Th>Código</Th>
                      <Th>Descrição</Th>
                      <Th>Ações</Th>
                    </tr>
                  </Thead>
                  <tbody>
                    {recursos.map((recurso) => (
                      <tr key={recurso.id}>
                        <Td>
                          <strong>{recurso.codigo}</strong>
                        </Td>
                        <Td>{recurso.descricao}</Td>
                        <Td>
                          <ActionsCell>
                            <GhostButton onClick={() => navigate(`/gestao/recursos/${recurso.id}/editar`)}>
                              <FiEdit />
                              Editar
                            </GhostButton>
                            <GhostButton onClick={() => abrirModalPlanos(recurso)}>
                              <FiBookOpen />
                              Ver planos
                            </GhostButton>
                          </ActionsCell>
                        </Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrapper>

              {pagination.totalPages > 1 && (
                <VeiculosPaginacao
                  pagination={pagination}
                  onPageChange={handlePageChange}
                  onItemsPerPageChange={handleItemsPerPageChange}
                  loading={loading}
                />
              )}
            </>
          )}
        </ContentCard>

        {modal.aberto && (
          <ModalOverlay onClick={fecharModal}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Planos que usam o recurso</ModalTitle>
                <CloseButton onClick={fecharModal}>
                  <FiX />
                </CloseButton>
              </ModalHeader>
              <ModalBody>
                <Subtitle>
                  {modal.recurso?.codigo} • {modal.recurso?.descricao}
                </Subtitle>
                {modal.loading && <EmptyText>Carregando planos associados...</EmptyText>}
                {modal.erro && <EmptyText>{modal.erro}</EmptyText>}
                {!modal.loading && !modal.erro && modal.planos.length === 0 && (
                  <EmptyText>Nenhum plano vinculado a este recurso.</EmptyText>
                )}
                {!modal.loading && modal.planos.length > 0 && (
                  <List>
                    {modal.planos.map((plano) => (
                      <ListItem key={plano.id}>
                        <strong>{plano.nome}</strong>
                        <span>Status: {plano.ativo ? 'Ativo' : 'Inativo'}</span>
                      </ListItem>
                    ))}
                  </List>
                )}
              </ModalBody>
              <ModalFooter>
                <Button $variant="secondary" onClick={fecharModal}>
                  Fechar
                </Button>
              </ModalFooter>
            </ModalContent>
          </ModalOverlay>
        )}
      </Container>
    </Layout>
  );
};

export default Recursos;


